/**
 * Changing a board's task-key prefix (commit ↔ task links, slice 2). Pure.
 *
 * Jira's rule on a project-key rename: the new key is used from now on, and
 * every old key keeps resolving — commits, branch names and links written
 * with APB-42 still find the task after the board became WEB. So the old
 * prefix moves into `keyPrefixAliases`; going back to an old prefix takes it
 * out of the aliases again (and parks the current one there).
 *
 * A prefix (or alias) another board the user can see already uses is refused:
 * WEB-3 must name one task, not two.
 */
import { validPrefix } from './taskKeys';

export const MAX_ALIASES = 20;

/** The board's old prefixes, valid and upper case, without duplicates. */
export function prefixAliasesOf(board) {
  const out = [];
  for (const p of Array.isArray(board?.keyPrefixAliases) ? board.keyPrefixAliases : []) {
    const v = validPrefix(p);
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

/** Current prefix and every alias of a board (or a board summary). */
export function allPrefixesOf(board) {
  const current = validPrefix(board?.keyPrefix);
  const aliases = prefixAliasesOf(board).filter((p) => p !== current);
  return current ? [current, ...aliases] : aliases;
}

/**
 * What saving `draft` as the prefix of `board` does.
 *   { ok: true, patch: null }                       nothing changes (blank, or the same prefix)
 *   { ok: true, patch: { keyPrefix, keyPrefixAliases } }
 *   { ok: false, error: 'invalid' | 'taken' }
 * `taken` = every prefix and alias of the OTHER boards the user can see.
 */
export function prefixChange(board, draft, taken = []) {
  const text = String(draft ?? '').trim();
  if (!text) return { ok: true, patch: null };
  const next = validPrefix(text);
  if (!next) return { ok: false, error: 'invalid' };
  const current = validPrefix(board?.keyPrefix);
  if (next === current) return { ok: true, patch: null };
  const others = new Set((taken || []).map((p) => String(p || '').toUpperCase()));
  if (others.has(next)) return { ok: false, error: 'taken' };
  const aliases = prefixAliasesOf(board).filter((p) => p !== next);
  if (current && !aliases.includes(current)) aliases.push(current);
  return { ok: true, patch: { keyPrefix: next, keyPrefixAliases: aliases.slice(-MAX_ALIASES) } };
}

/** Every prefix and alias of the boards in `summaries` except `boardId`. */
export function prefixesTakenByOthers(summaries, boardId) {
  const out = [];
  for (const b of summaries || []) {
    if (!b || String(b.id) === String(boardId)) continue;
    for (const p of allPrefixesOf(b)) if (!out.includes(p)) out.push(p);
  }
  return out;
}
